import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import { useFormik } from "formik";
import { createDeal, fetchDeals, addProductToDeal } from "../../../State/admin/DealSlice";
import { fetchSellerProducts } from "../../../State/seller/sellerProductSlice";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import api from "../../../config/Api";
import { fetchAllAdminProducts } from "../../../State/admin/adminSlice";

const CreateDealForm = () => {
  const dispatch = useAppDispatch();

  const dealState = useAppSelector((store: any) => store.deal);
  const sellerProductState = useAppSelector((store: any) => store.sellerProduct);
  const adminState = useAppSelector((store: any) => store.admin);

  const products = adminState?.products?.length
    ? adminState.products
    : sellerProductState?.products || [];

  const [mode, setMode] = useState<"CREATE_NEW" | "ADD_PRODUCT">("CREATE_NEW");
  const [sellers, setSellers] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [mainCategories, setMainCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [itemCategories, setItemCategories] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getId = (obj: any): string => {
    if (!obj) return "";
    if (typeof obj === "string" || typeof obj === "number") return String(obj);
    return String(obj.categoryId || obj.id || "");
  };

  useEffect(() => {
    dispatch(fetchAllAdminProducts());
    dispatch(fetchDeals());

    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("jwt");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [sellersRes, categoriesRes] = await Promise.all([
          api.get("/sellers", { headers }),
          api.get("/categories", { headers }),
        ]);

        const rawCategories = categoriesRes.data || [];
        setSellers(sellersRes.data || []);
        setAllCategories(rawCategories);

        const level1 = rawCategories.filter((c: any) => {
          const parentId = getId(c.parentCategory);
          return (c.level === 1 || (!parentId && !c.level)) && !c.parentCategory;
        });
        setMainCategories(level1);
      } catch (err) {
        console.error("Failed to load initial data:", err);
      }
    };

    fetchInitialData();
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      selectedDealId: "",
      title: "",
      discount: 0,
      sellerId: "",
      mainCategory: "",
      subCategory: "",
      itemCategory: "",
      productId: "",
    },
    onSubmit: async (values, { resetForm }) => {
      setFormError(null);
      setSuccessMsg(null);
      setIsSubmitting(true);

      try {
        const selectedCatId = values.itemCategory || values.subCategory || values.mainCategory;

        if (mode === "CREATE_NEW") {
          if (!values.title.trim()) {
            setFormError("Title is required to create a deal.");
            setIsSubmitting(false);
            return;
          }

          const reqData: any = {
            title: values.title,
            discount: Number(values.discount),
          };

          if (selectedCatId) {
            reqData.category = { id: Number(selectedCatId) };
          }

          const dealResult = await dispatch(createDeal(reqData)).unwrap();

          if (dealResult?.id && values.productId) {
            await dispatch(
              addProductToDeal({
                dealId: Number(dealResult.id),
                productId: Number(values.productId),
              })
            ).unwrap();
          }
          setSuccessMsg("Deal created successfully!");
        } else {
          if (!values.selectedDealId || !values.productId) {
            setFormError("Please select both a deal and a product.");
            setIsSubmitting(false);
            return;
          }

          await dispatch(
            addProductToDeal({
              dealId: Number(values.selectedDealId),
              productId: Number(values.productId),
            })
          ).unwrap();
          setSuccessMsg("Product added to deal successfully!");
        }

        resetForm();
        dispatch(fetchDeals());
      } catch (error: any) {
        console.error("Submission failed:", error);
        setFormError(
          typeof error === "string" ? error : error?.message || "Failed to execute deal operation"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const filterProductsList = (
    sellerId: string,
    mainCatId: string,
    subCatId: string,
    itemCatId: string,
    productsSource = products
  ) => {
    let result = [...productsSource];

    if (sellerId) {
      result = result.filter((p: any) => {
        const pSellerId = getId(p.seller) || String(p.sellerId || "");
        return pSellerId === String(sellerId);
      });
    }

    const targetCatId = itemCatId || subCatId || mainCatId;

    if (targetCatId) {
      result = result.filter((p: any) => {
        const cat = p.category;
        if (!cat) return false;

        const pCatId = getId(cat);
        const pParentId = getId(cat.parentCategory) || getId(cat.parentCategoryId);
        const pGrandParentId = getId(cat.parentCategory?.parentCategory);

        return (
          pCatId === String(targetCatId) ||
          pParentId === String(targetCatId) ||
          pGrandParentId === String(targetCatId)
        );
      });
    }

    setFilteredProducts(result);
  };

  useEffect(() => {
    filterProductsList(
      formik.values.sellerId,
      formik.values.mainCategory,
      formik.values.subCategory,
      formik.values.itemCategory,
      products
    );
  }, [
    products,
    formik.values.sellerId,
    formik.values.mainCategory,
    formik.values.subCategory,
    formik.values.itemCategory,
  ]);

  const handleMainCategoryChange = (e: any) => {
    const mainCatVal = String(e.target.value);
    formik.setFieldValue("mainCategory", mainCatVal);
    formik.setFieldValue("subCategory", "");
    formik.setFieldValue("itemCategory", "");
    formik.setFieldValue("productId", "");
    setSubCategories([]);
    setItemCategories([]);

    if (mainCatVal) {
      const selectedMain = allCategories.find((c: any) => getId(c) === mainCatVal);
      let subCats = selectedMain?.levelTwoCategories || selectedMain?.children || [];

      if (!subCats.length) {
        subCats = allCategories.filter((c: any) => {
          const parentId = getId(c.parentCategory) || getId(c.parentCategoryId);
          return parentId === mainCatVal;
        });
      }

      setSubCategories(subCats);
    }
  };

  const handleSubCategoryChange = (e: any) => {
    const subCatVal = String(e.target.value);
    formik.setFieldValue("subCategory", subCatVal);
    formik.setFieldValue("itemCategory", "");
    formik.setFieldValue("productId", "");
    setItemCategories([]);

    if (subCatVal) {
      const selectedSub =
        subCategories.find((sc: any) => getId(sc) === subCatVal) ||
        allCategories.find((c: any) => getId(c) === subCatVal);

      let itemCats = selectedSub?.levelThreeCategories || selectedSub?.children || [];

      if (!itemCats.length) {
        itemCats = allCategories.filter((c: any) => {
          const parentId = getId(c.parentCategory) || getId(c.parentCategoryId);
          return parentId === subCatVal;
        });
      }

      setItemCategories(itemCats);
    }
  };

  const handleItemCategoryChange = (e: any) => {
    const itemCatId = String(e.target.value);
    formik.setFieldValue("itemCategory", itemCatId);
    formik.setFieldValue("productId", "");
  };

  const handleSellerChange = (e: any) => {
    const sellerId = String(e.target.value);
    formik.setFieldValue("sellerId", sellerId);
    formik.setFieldValue("productId", "");

    if (sellerId) {
      const jwt = localStorage.getItem("jwt") || "";
      dispatch(fetchSellerProducts({ jwt, sellerId }));
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={formik.handleSubmit}
      className="space-y-4 p-6 bg-white rounded-lg shadow-md max-w-lg mx-auto w-full"
    >
      {formError && <Alert severity="error">{formError}</Alert>}
      {successMsg && <Alert severity="success">{successMsg}</Alert>}

      <ToggleButtonGroup
        color="primary"
        value={mode}
        exclusive
        fullWidth
        size="small"
        onChange={(_, newMode) => newMode && setMode(newMode)}
      >
        <ToggleButton value="CREATE_NEW">Create Deal</ToggleButton>
        <ToggleButton value="ADD_PRODUCT">Add Product</ToggleButton>
      </ToggleButtonGroup>

      {mode === "ADD_PRODUCT" && (
        <FormControl fullWidth size="small">
          <InputLabel id="select-deal-label">Select Deal</InputLabel>
          <Select
            labelId="select-deal-label"
            name="selectedDealId"
            value={formik.values.selectedDealId}
            label="Select Deal"
            onChange={formik.handleChange}
          >
            {dealState?.deals?.map((item: any) => (
              <MenuItem key={item.id} value={item.id}>
                {item.title} ({item.discount}% Off)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {mode === "CREATE_NEW" && (
        <>
          <TextField
            fullWidth
            size="small"
            name="title"
            label="Deal Title"
            value={formik.values.title}
            onChange={formik.handleChange}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            name="discount"
            label="Discount Percentage (%)"
            value={formik.values.discount}
            onChange={formik.handleChange}
          />
        </>
      )}

      <Divider sx={{ my: 1 }}>PRODUCT FILTER & SELECTION</Divider>

      <FormControl fullWidth size="small">
        <InputLabel id="seller-select-label">Seller</InputLabel>
        <Select
          labelId="seller-select-label"
          name="sellerId"
          value={formik.values.sellerId}
          label="Seller"
          onChange={handleSellerChange}
        >
          <MenuItem value="">
            <em>All Sellers</em>
          </MenuItem>
          {sellers.map((s: any) => (
            <MenuItem key={s.id} value={s.id}>
              {s.sellerName || s.businessName || `Seller #${s.id}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel id="main-category-label">Main Category</InputLabel>
        <Select
          labelId="main-category-label"
          name="mainCategory"
          value={formik.values.mainCategory}
          label="Main Category"
          onChange={handleMainCategoryChange}
        >
          <MenuItem value="">
            <em>All Main Categories</em>
          </MenuItem>
          {mainCategories.map((c: any) => (
            <MenuItem key={getId(c)} value={getId(c)}>
              {c.name || c.categoryName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" disabled={!subCategories.length}>
        <InputLabel id="sub-category-label">Sub-Category</InputLabel>
        <Select
          labelId="sub-category-label"
          name="subCategory"
          value={formik.values.subCategory}
          label="Sub-Category"
          onChange={handleSubCategoryChange}
        >
          <MenuItem value="">
            <em>All Sub-Categories</em>
          </MenuItem>
          {subCategories.map((sc: any) => (
            <MenuItem key={getId(sc)} value={getId(sc)}>
              {sc.name || sc.categoryName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" disabled={!itemCategories.length}>
        <InputLabel id="item-category-label">Item Category</InputLabel>
        <Select
          labelId="item-category-label"
          name="itemCategory"
          value={formik.values.itemCategory}
          label="Item Category"
          onChange={handleItemCategoryChange}
        >
          <MenuItem value="">
            <em>All Item Categories</em>
          </MenuItem>
          {itemCategories.map((ic: any) => (
            <MenuItem key={getId(ic)} value={getId(ic)}>
              {ic.name || ic.categoryName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel id="product-select-label">
          Select Product ({filteredProducts.length} available)
        </InputLabel>
        <Select
          labelId="product-select-label"
          name="productId"
          value={formik.values.productId}
          label={`Select Product (${filteredProducts.length} available)`}
          onChange={formik.handleChange}
        >
          {filteredProducts.length > 0 ? (
            filteredProducts.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>
                {p.title} - (${p.sellingPrice})
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              <em>No products available for selected filter</em>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      <Button
        fullWidth
        sx={{ py: 1 }}
        type="submit"
        variant="contained"
        disabled={isSubmitting || dealState?.loading}
      >
        {mode === "CREATE_NEW" ? "Create Deal" : "Add Product to Deal"}
      </Button>
    </Box>
  );
};

export default CreateDealForm;